import { Dtos } from "@inrev/common";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useRequest } from "../../../utils/request";
import {
	bondRequestPreviewsQueryKey,
	bondRequestsQueryKey,
} from "../../bond-request/bond-request.keys";
import { accountPreviewsQueryKey } from "../../previews/previews.keys";
import { accountQueryKey } from "../account.keys";

type SubmitSuretyAccountVariables = {
	data: Dtos.Admin.SuretyAccount.Create.Request;
	onSuccess: (account: Dtos.Admin.Account.Create.Response) => void;
	onError?: (error: Error) => void;
};

export const useAdminSubmitSuretyAccount = () => {
	const { post } = useRequest();
	const queryClient = useQueryClient();
	const { showSuccessFlag } = useFlags();
	const navigate = useNavigate();

	const mutation = useMutation({
		mutationKey: ["adminAccountSubmission"],
		mutationFn: async ({ data }: SubmitSuretyAccountVariables) => {
			const response = await post<Dtos.Admin.Account.Create.Response>(
				"/v2/admin/surety/accounts",
				data,
			);
			return response;
		},
		onSuccess: (data, { onSuccess }) => {
			queryClient.invalidateQueries({ queryKey: bondRequestsQueryKey() });
			queryClient.invalidateQueries({ queryKey: accountQueryKey(data.id) });
			queryClient.invalidateQueries({ queryKey: bondRequestPreviewsQueryKey() });
			queryClient.refetchQueries({ queryKey: accountPreviewsQueryKey() });
			showSuccessFlag({
				message: "Account Submitted Successfully",
				actions: [
					{
						label: "View Account",
						onClick: () => navigate(`/accounts/${data.id}`),
						actionId: `${data.id}`,
					},
				],
				autoDismiss: true,
			});

			if (data.quote) {
				showSuccessFlag({
					message: "Bond Request Submitted Successfully",
					actions: [
						{
							label: "View Request",
							onClick: () => navigate(`/accounts/${data.id}/requests/${data.quote?.id}`),
							actionId: `${data.quote.id}`,
						},
					],
					autoDismiss: true,
				});
			}

			onSuccess(data);
			return data;
		},
	});

	// Per-call onError, not a mutation-level one: an onError in the useMutation
	// options makes QueryClientProvider skip Sentry logging and the global error
	// modal for every failure of this mutation.
	const { mutate } = mutation;
	const submitAccount = useCallback(
		(variables: SubmitSuretyAccountVariables) => mutate(variables, { onError: variables.onError }),
		[mutate],
	);

	return {
		submitAccount,
		isSubmitting: mutation.isPending,
	};
};
